import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import tes from '../assets/img/test1.png';
import tes2 from '../assets/img/Test6.png';
import tes3 from '../assets/img/Test3.png';
import tes4 from '../assets/img/Test4.png';
import tes5 from '../assets/img/Test7.png';

const TestimonialSection = () => {
  const testimonials = [
    {
      text: "As a new business, we required to build our online presence. Ato Creative assisted us with the website, brand design, and social media launch. Due to their help, we are now experiencing growth.",
      author: "Solomon Gebremariam",
      position: "Chief Technology Officer",
      photoUrl: tes
    },
    {
      text: "Ato Creative created our new brand look. This changed our business. We now have 30% more leads. They understood our vision and made a design that people connect with.",
      author: "Tewodros Kebede",
      position: "General Manager",
      photoUrl: tes2
    },
    {
      text: "The team at Ato Creative responds quickly and is flexible. They were able to adjust to our changes and provided good work within short timeframes.",
      author: "Selamawit Mekonnen",
      position: "Senior Consultant & CEO",
      photoUrl: tes3
    },
    {
      text: "Ato Creative's SEO and social media increased our online store visits by 70% in three months, resulting in more sales. We're very satisfied.",
      author: "Dr. Dawit Solomon",
      position: "Operations Director",
      photoUrl: tes4
    },
    {
      text: "We knew we needed better content, but lacked the knowledge. Ato Creative provided a strategy, resulting in more engagement and greater brand awareness through their blog posts and emails.",
      author: "Meron Hailu",
      position: "Creative Director",
      photoUrl: tes5
    }
  ];

  return (
    <div className="py-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
      {/* Section Header */}
      <motion.div 
        className="mb-12"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 inline-block relative pb-2">
          What our clients say
          <span className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-[#FD5F20] to-[#FF8C00]"></span>
        </h1>
        <p className="mt-4 text-lg text-gray-600 max-w-3xl">
          Don't just take our word for it. Here's what our clients have to say about working with us.
        </p>
      </motion.div>

      {/* Testimonial Carousel */}
      <TestimonialCarousel testimonials={testimonials} />
    </div>
  );
};

const TestimonialCarousel = ({ testimonials }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState('right');
  const [isHovered, setIsHovered] = useState(false);
  const [ref, inView] = useInView({ threshold: 0.1, triggerOnce: false });

  // Number of cards to show based on screen size
  const [cardsToShow, setCardsToShow] = useState(2);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 640) {
        setCardsToShow(1);
      } else {
        setCardsToShow(2);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Auto-advance carousel
  useEffect(() => {
    if (!inView || isHovered) return;

    const interval = setInterval(() => {
      setDirection('right');
      setCurrentIndex((prevIndex) => 
        prevIndex === testimonials.length - cardsToShow ? 0 : prevIndex + 1
      );
    }, 5000);

    return () => clearInterval(interval);
  }, [inView, isHovered, testimonials.length, cardsToShow]);

  const nextSlide = () => {
    setDirection('right');
    setCurrentIndex((prevIndex) => 
      prevIndex === testimonials.length - cardsToShow ? 0 : prevIndex + 1
    );
  };

  const prevSlide = () => {
    setDirection('left');
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? testimonials.length - cardsToShow : prevIndex - 1
    );
  };

  // Calculate visible testimonials
  const visibleTestimonials = [];
  for (let i = 0; i < cardsToShow; i++) {
    const index = (currentIndex + i) % testimonials.length;
    visibleTestimonials.push(testimonials[index]);
  }

  return (
    <div 
      ref={ref}
      className="relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Navigation Arrows */}
      <button 
        onClick={prevSlide}
        className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white p-2 rounded-full shadow-lg hover:bg-gray-100 transition-colors duration-200"
        aria-label="Previous testimonial"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-[#FD5F20]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
      </button>
      
      <button 
        onClick={nextSlide}
        className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white p-2 rounded-full shadow-lg hover:bg-gray-100 transition-colors duration-200"
        aria-label="Next testimonial"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-[#FD5F20]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>

      {/* Carousel Container with Fixed Height */}
      <div className="overflow-hidden px-10">
        <div className="flex justify-center">
          <div className="w-full relative" style={{ minHeight: '400px' }}>
            <AnimatePresence mode="wait" custom={direction}>
              <motion.div
                key={currentIndex}
                className="flex gap-6 w-full absolute inset-0"
                initial={{ x: direction === 'right' ? '100%' : '-100%', opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: direction === 'right' ? '-100%' : '100%', opacity: 0 }}
                transition={{ duration: 0.5 }}
              >
                {visibleTestimonials.map((testimonial, index) => (
                  <div 
                    key={`${currentIndex}-${index}`}
                    className={`${cardsToShow === 1 ? 'w-full' : 'w-1/2'} px-2`}
                  >
                    <TestimonialCard 
                      text={testimonial.text}
                      author={testimonial.author}
                      position={testimonial.position}
                      photoUrl={testimonial.photoUrl}
                      index={index}
                    />
                  </div>
                ))}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Indicators */}
      <div className="flex justify-center mt-6 gap-2">
        {Array.from({ length: testimonials.length - cardsToShow + 1 }).map((_, index) => (
          <button
            key={index}
            onClick={() => {
              setDirection(index > currentIndex ? 'right' : 'left');
              setCurrentIndex(index);
            }}
            className={`h-2 rounded-full transition-all duration-300 ${index === currentIndex ? 'w-6 bg-[#FD5F20]' : 'w-2 bg-gray-300'}`}
            aria-label={`Go to testimonial ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

const TestimonialCard = ({ text, author, position, photoUrl, index }) => {
  return (
    <motion.div
      className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100 hover:shadow-xl transition-all duration-300 flex flex-col h-full"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      viewport={{ once: true }}
      whileHover={{ y: -5 }}
    >
      {/* Quote Icon */}
      <div className="absolute top-6 left-6 text-[#FD5F20] opacity-20">
        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
          <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
        </svg>
      </div>

      {/* Testimonial Content */}
      <div className="p-6 pt-12 flex-1">
        <p className="text-gray-700 text-lg leading-relaxed mb-6 relative z-10">
          {text}
        </p>
      </div>

      {/* Author Info */}
      <div className="px-6 pb-6 flex items-center">
        <div className="relative">
          <img
            className="w-12 h-12 object-cover rounded-full border-2 border-white shadow-md"
            src={photoUrl}
            alt={`Ato creative Testimonial from ${author}`}
          />
          <div className="absolute -bottom-1 -right-1 bg-[#FD5F20] rounded-full p-1">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          </div>
        </div>
        <div className="ml-4">
          <h3 className="font-bold text-gray-900">{author}</h3>
          <p className="text-gray-500 text-sm">{position}</p>
        </div>
      </div>
    </motion.div>
  );
};

export default TestimonialSection;